// @ts-nocheck
// Modal de suporte — relatar bug / mau funcionamento.
// Aberto pelo icone "support" no header da Home.
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './icons';
import { Sheet } from './common';
import { AuthSession } from '../types';

const MAX_SCREENSHOTS = 3;
const API = '/api';

interface Props {
  open: boolean;
  onClose: () => void;
  session: AuthSession | null;
}

export const SupportSheet: React.FC<Props> = ({ open, onClose, session }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setTitle(''); setMessage(''); setScreenshots([]); setPreviews([]);
      setError(null); setSaving(false); setSuccess(false);
    }
  }, [open]);

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files).slice(0, MAX_SCREENSHOTS - screenshots.length);
    const validImages = arr.filter(f => f.type.startsWith('image/'));
    if (validImages.length === 0) return;
    const newPreviews = validImages.map(f => URL.createObjectURL(f));
    setScreenshots(prev => [...prev, ...validImages].slice(0, MAX_SCREENSHOTS));
    setPreviews(prev => [...prev, ...newPreviews].slice(0, MAX_SCREENSHOTS));
  };

  const removeAt = (idx: number) => {
    setScreenshots(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => {
      const url = prev[idx];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const submit = async () => {
    if (saving) return;
    const t = title.trim();
    const m = message.trim();
    if (!t) { setError('Dê um título curto pro problema.'); return; }
    if (m.length < 10) { setError('Descreva o problema com pelo menos 10 caracteres.'); return; }
    if (!session?.accessToken) { setError('Sessão expirada. Faça login novamente.'); return; }

    setSaving(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('title', t);
      fd.append('message', m);
      // Plataforma e versão ajudam o admin a diagnosticar
      const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 200);
      fd.append('platform', /iPhone|iPad|iPod/i.test(ua) ? 'ios' : /Android/i.test(ua) ? 'android' : 'web');
      const meta = document.querySelector('meta[name="build-version"]') as HTMLMetaElement | null;
      if (meta?.content) fd.append('appVersion', meta.content);
      screenshots.forEach(f => fd.append('screenshots', f));

      const r = await fetch(`${API}/support/tickets`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.accessToken}` },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((data as any).error || 'Erro ao enviar');

      setSuccess(true);
      // Fecha apos breve confirmacao visual
      setTimeout(() => onClose(), 1500);
    } catch (e: any) {
      setError(e?.message || 'Erro ao enviar ticket');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onClose={() => !saving && onClose()}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Suporte</div>
          <div className="serif" style={{ fontSize: 26 }}>Relate um problema</div>
        </div>
        <button onClick={() => !saving && onClose()} style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'var(--paper-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><Icon name="close" size={16}/></button>
      </div>

      {success ? (
        <div style={{
          padding: '32px 16px', textAlign: 'center',
          borderRadius: 16, background: 'rgba(94,113,86,0.12)', border: '1px solid rgba(94,113,86,0.28)',
          marginTop: 12,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'var(--sage)', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <Icon name="check" size={22} color="white" stroke={2.6}/>
          </div>
          <div className="serif" style={{ fontSize: 20, lineHeight: 1.2 }}>Ticket enviado</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 6 }}>
            Obrigado! Vamos olhar e responder o quanto antes.
          </div>
        </div>
      ) : (
        <>
          <div style={{
            padding: '10px 14px', borderRadius: 12,
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.45,
            marginTop: 4,
          }}>
            Descreva o problema com o máximo de detalhe possível. Anexe até 3 prints se ajudar.
          </div>

          <div style={{ marginTop: 14 }}>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 6 }}>
              Título
            </div>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Ex: card de clube sem capa"
              maxLength={200}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 14,
                border: '1px solid var(--line)',
                background: 'var(--paper-2)',
                fontSize: 15, fontFamily: 'inherit', color: 'var(--ink)',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 6 }}>
              Descrição
            </div>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="O que aconteceu? O que você esperava que acontecesse?"
              maxLength={5000}
              rows={6}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 14,
                border: '1px solid var(--line)',
                background: 'var(--paper-2)',
                fontSize: 15, fontFamily: 'inherit', color: 'var(--ink)',
                outline: 'none', boxSizing: 'border-box',
                resize: 'vertical', minHeight: 110,
              }}
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 8 }}>
              Imagens (opcional · até 3)
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {previews.map((url, i) => (
                <div key={i} style={{
                  position: 'relative', width: 76, height: 76, borderRadius: 10,
                  overflow: 'hidden', border: '1px solid var(--line)',
                  background: `url(${url}) center/cover no-repeat var(--paper-2)`,
                }}>
                  <button onClick={() => removeAt(i)} aria-label="Remover" style={{
                    position: 'absolute', top: 4, right: 4,
                    width: 22, height: 22, borderRadius: '50%',
                    background: 'rgba(20,16,14,0.78)', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: 'none',
                  }}>
                    <Icon name="close" size={12} color="white"/>
                  </button>
                </div>
              ))}
              {screenshots.length < MAX_SCREENSHOTS && (
                <button onClick={() => fileRef.current?.click()} style={{
                  width: 76, height: 76, borderRadius: 10,
                  background: 'var(--paper-2)',
                  border: '1.5px dashed var(--line-strong)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                  color: 'var(--ink-3)', fontSize: 11,
                }}>
                  <Icon name="plus" size={16} color="var(--ink-3)" stroke={2}/>
                  Anexar
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => { addFiles(e.target.files); if (fileRef.current) fileRef.current.value = ''; }}
              />
            </div>
          </div>

          {error && (
            <div style={{
              marginTop: 14, padding: '10px 14px', borderRadius: 12,
              background: 'rgba(199,83,26,0.10)', border: '1px solid rgba(199,83,26,0.22)',
              color: 'var(--ember)', fontSize: 13, lineHeight: 1.4,
            }}>
              {error}
            </div>
          )}

          <button onClick={submit} disabled={saving} style={{
            width: '100%', marginTop: 18,
            background: saving ? 'var(--paper-3)' : 'var(--ember)',
            color: saving ? 'var(--ink-4)' : 'var(--paper)',
            padding: '16px', borderRadius: 16, fontSize: 15, fontWeight: 500,
            border: 'none',
            boxShadow: saving ? 'none' : '0 14px 28px -10px rgba(199,83,26,0.55)',
          }}>
            {saving ? 'Enviando...' : 'Enviar ticket'}
          </button>
        </>
      )}
    </Sheet>
  );
};
